import { apiClient } from './client';

export interface Currency {
  id: string;
  companyId?: string | null;
  code: string;
  name: string;
  symbol: string;
  exchangeRate: number;
  isBaseCurrency?: boolean;
  isActive: boolean;
  lastUpdated: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateCurrencyPayload {
  code: string;
  name: string;
  symbol?: string;
  exchangeRate: number;
  isActive?: boolean;
  isBaseCurrency?: boolean;
}

export interface UpdateCurrencyPayload {
  code?: string;
  name?: string;
  symbol?: string;
  exchangeRate?: number;
  isActive?: boolean;
}

export interface CurrencyApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

export const currencyApi = {
  /**
   * Fetch all currencies with optional search and active status filter
   */
  getAll: async (params?: { search?: string; isActive?: boolean | string }): Promise<Currency[]> => {
    const res = await apiClient.get<CurrencyApiResponse<Currency[]>>('/api/v1/finance/currencies', {
      params,
    });
    return res.data.data || [];
  },

  /**
   * Fetch a single currency by ID
   */
  getById: async (id: string): Promise<Currency> => {
    const res = await apiClient.get<CurrencyApiResponse<Currency>>(`/api/v1/finance/currencies/${id}`);
    return res.data.data;
  },

  /**
   * Create a new currency
   */
  create: async (payload: CreateCurrencyPayload): Promise<Currency> => {
    const res = await apiClient.post<CurrencyApiResponse<Currency>>('/api/v1/finance/currencies', payload);
    return res.data.data;
  },

  /**
   * Update full currency attributes
   */
  update: async (id: string, payload: UpdateCurrencyPayload): Promise<Currency> => {
    const res = await apiClient.put<CurrencyApiResponse<Currency>>(`/api/v1/finance/currencies/${id}`, payload);
    return res.data.data;
  },

  /**
   * Quick update of exchange rate
   */
  updateRate: async (id: string, exchangeRate: number): Promise<Currency> => {
    const res = await apiClient.patch<CurrencyApiResponse<Currency>>(`/api/v1/finance/currencies/${id}/rate`, {
      exchangeRate,
    });
    return res.data.data;
  },

  /**
   * Toggle active / inactive status
   */
  toggleStatus: async (id: string): Promise<Currency> => {
    const res = await apiClient.patch<CurrencyApiResponse<Currency>>(`/api/v1/finance/currencies/${id}/toggle`);
    return res.data.data;
  },

  /**
   * Delete a currency
   */
  delete: async (id: string): Promise<{ id: string; code: string; deleted: boolean }> => {
    const res = await apiClient.delete<CurrencyApiResponse<{ id: string; code: string; deleted: boolean }>>(
      `/api/v1/finance/currencies/${id}`
    );
    return res.data.data;
  },
};

export default currencyApi;
